<template>
    <ModalContent
        flat-chrome
        :help-content-id="`popup-catalog-${current_entity_profile}`"
    >
        <template slot="title">{{ title }}</template>
        <template slot="content" v-if="catalog"
            ><div class="stitch-modal general-settings-reference stitch-form">
                <v-alert
                    v-if="catalog_saving"
                    type="info"
                    outlined
                    prominent
                    class="catalog-saving-alert mb-4"
                >
                    <div class="d-flex align-center">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="28"
                            width="3"
                            class="mr-4 flex-shrink-0"
                        />
                        <div>
                            <div class="text-subtitle-2 font-weight-medium">
                                {{ savingTitle }}
                            </div>
                            <div
                                class="text-body-2 grey--text text--darken-1 mb-0"
                            >
                                {{ savingMessage }}
                            </div>
                        </div>
                    </div>
                </v-alert>

                <div
                    class="catalog-modal-form stitch-modal__stack"
                    :class="{ 'catalog-modal-form--saving': catalog_saving }"
                >
                    <v-card
                        class="stitch-modal__card"
                        elevation="0"
                        v-if="
                            ((stats.providers && isNew) || catalog.provider) &&
                            !params.ownedOnly
                        "
                    >
                        <v-card-text
                            ><h5 class="stitch-modal__title">
                                <v-icon size="16">mdi-domain</v-icon
                                ><span>{{ $translate("seller") }}</span>
                            </h5>
                            <p v-if="isNew">
                                {{ $translate("sellerInstructions") }}
                            </p>
                            <v-row>
                                <v-col cols="12" md="12" class="mt-2">
                                    <SearchProvider
                                        outlined
                                        :disabled="!isNew"
                                        class="mt-0"
                                        :value="catalog.provider"
                                        @select="updateProvider"
                                        :label="$translate('labels.seller')"
                                        :required="true"
                                        :dense="true"
                                        :error-messages="
                                            catalog_saving_errors.provider
                                        "
                                    />
                                </v-col>
                            </v-row> </v-card-text
                    ></v-card>
                    <template v-if="isConnectedSeller()">
                        <Warning :elevation="2">
                            {{ $translate("connectedSellerInstructions") }}
                        </Warning>
                    </template>
                    <template v-else>
                        <v-card class="stitch-modal__card" elevation="0"
                            ><v-card-text
                                ><h5 class="stitch-modal__title">
                                    <v-icon size="16"
                                        >mdi-book-open-outline</v-icon
                                    ><span>{{
                                        $translate("informations")
                                    }}</span>
                                </h5>
                                <v-row class="mt-2">
                                    <v-col cols="12" md="6">
                                        <label
                                            class="catalog-field-label"
                                            for="catalog-name"
                                        >
                                            {{
                                                requiredLabel(
                                                    $translate("labels.name")
                                                )
                                            }}
                                        </label>
                                        <v-text-field
                                            outlined
                                            v-model="catalog.name"
                                            id="catalog-name"
                                            required
                                            hide-details="auto"
                                            :error-messages="
                                                catalog_saving_errors.name
                                            "
                                            :autofocus="true"
                                            :dense="true"
                                        />
                                    </v-col>
                                    <template v-if="isNew && stats.catalogs">
                                        <v-col cols="12" md="6">
                                            <SearchCatalog
                                                outlined
                                                :value="catalog.copy_from"
                                                @select="catalogSelected"
                                                :dense="true"
                                                :label="
                                                    $translate(
                                                        'labels.copyFrom'
                                                    )
                                                "
                                                :key="catalog.provider"
                                                :additional-params="
                                                    getCatalogsParams()
                                                "
                                            />
                                        </v-col>
                                    </template>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <label
                                            class="catalog-field-label"
                                            for="catalog-vat"
                                        >
                                            {{
                                                $translate(
                                                    "labels.forceVatRate"
                                                ).replace(
                                                    "{vat}",
                                                    defaultVatRate
                                                )
                                            }}
                                        </label>
                                        <v-text-field
                                            outlined
                                            :value="forceVatRate"
                                            @input="
                                                catalog.force_vat_rate = $event
                                                    ? decimal(
                                                          $utils.normalizeDecimalString(
                                                              $event
                                                          )
                                                      )
                                                          .dividedBy(100)
                                                          .toString()
                                                    : null
                                            "
                                            type="text"
                                            inputmode="decimal"
                                            id="catalog-vat"
                                            clearable
                                            dense
                                            hide-details="auto"
                                            :error-messages="
                                                catalog_saving_errors.forceVatRate
                                            "
                                        />
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <label
                                            class="catalog-field-label"
                                            for="catalog-price-type"
                                        >
                                            {{
                                                requiredLabel(
                                                    $translate(
                                                        "labels.priceType"
                                                    )
                                                )
                                            }}
                                        </label>
                                        <v-select
                                            outlined
                                            v-model="catalog.prices_include_vat"
                                            :dense="true"
                                            id="catalog-price-type"
                                            :items="priceTypeItems"
                                            :placeholder="
                                                isNew
                                                    ? $translate(
                                                          'labels.selectPriceType'
                                                      )
                                                    : undefined
                                            "
                                            :clearable="false"
                                            hide-details="auto"
                                            :error-messages="
                                                pricesIncludeVatErrors
                                            "
                                            @blur="
                                                pricesIncludeVatTouched = true
                                            "
                                        />
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <label
                                            class="catalog-field-label"
                                            for="catalog-margin"
                                        >
                                            {{
                                                $translate(
                                                    "labels.targetMarginPercent"
                                                )
                                            }}
                                            <StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.targetMarginPercentHint'
                                                    )
                                                "
                                        /></label>
                                        <v-text-field
                                            outlined
                                            v-model="targetMarginPercentDisplay"
                                            type="text"
                                            inputmode="decimal"
                                            id="catalog-margin"
                                            dense
                                            hide-details="auto"
                                            :error-messages="
                                                catalog_saving_errors.target_margin_percent
                                            "
                                        />
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <label
                                            class="catalog-field-label"
                                            for="catalog-commission"
                                        >
                                            {{
                                                $translate(
                                                    "labels.agentCommissionPercent"
                                                )
                                            }}
                                            <StitchHint
                                                :hint="
                                                    $translate(
                                                        'labels.agentCommissionPercentHint'
                                                    )
                                                "
                                        /></label>
                                        <v-text-field
                                            outlined
                                            v-model="
                                                agentCommissionPercentDisplay
                                            "
                                            type="text"
                                            inputmode="decimal"
                                            id="catalog-commission"
                                            dense
                                            hide-details="auto"
                                            :error-messages="
                                                catalog_saving_errors.agent_commission_percent
                                            "
                                        />
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <template v-if="isNew">
                                        <v-col cols="12" md="6">
                                            <label
                                                class="catalog-field-label"
                                                for="catalog-default"
                                            >
                                                {{
                                                    requiredLabel(
                                                        $translate(
                                                            "labels.assignToNewCustomers"
                                                        )
                                                    )
                                                }}
                                                <StitchHint
                                                    :hint="
                                                        $translate(
                                                            'labels.assignToNewCustomersHint'
                                                        )
                                                    "
                                            /></label>
                                            <v-select
                                                outlined
                                                v-model="catalog.default"
                                                :dense="true"
                                                id="catalog-default"
                                                :items="yesNoItems"
                                                :placeholder="
                                                    $translate(
                                                        'labels.selectYesNo'
                                                    )
                                                "
                                                :clearable="false"
                                                hide-details="auto"
                                                :error-messages="
                                                    defaultCatalogErrors
                                                "
                                                @blur="
                                                    defaultCatalogTouched = true
                                                "
                                            />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <label
                                                class="catalog-field-label"
                                                for="catalog-auto-products"
                                            >
                                                {{
                                                    requiredLabel(
                                                        autoAddProductsLabel
                                                    )
                                                }}
                                                <StitchHint
                                                    :hint="autoAddProductsHint"
                                            /></label>
                                            <v-select
                                                outlined
                                                v-model="
                                                    catalog.auto_add_products
                                                "
                                                :dense="true"
                                                id="catalog-auto-products"
                                                :items="yesNoItems"
                                                :placeholder="
                                                    $translate(
                                                        'labels.selectYesNo'
                                                    )
                                                "
                                                :clearable="false"
                                                hide-details="auto"
                                                :error-messages="
                                                    autoAddProductsErrors
                                                "
                                                @blur="
                                                    autoAddProductsTouched = true
                                                "
                                            />
                                        </v-col>
                                    </template>
                                    <v-col v-else cols="12">
                                        <v-checkbox
                                            v-model="catalog.default"
                                            :label="
                                                $translate(
                                                    'labels.defaultCatalog'
                                                )
                                            "
                                            dense
                                            hide-details
                                            :error-messages="
                                                catalog_saving_errors.default
                                            "
                                        />
                                        <v-checkbox
                                            v-model="catalog.auto_add_products"
                                            :label="
                                                $translate(
                                                    'labels.autoAddProducts'
                                                )
                                            "
                                            dense
                                            hide-details
                                            :error-messages="
                                                catalog_saving_errors.auto_add_products
                                            "
                                        />
                                    </v-col>
                                </v-row>

                                <!-- Stock selection and ordering -->
                            </v-card-text></v-card
                        ><v-card class="stitch-modal__card" elevation="0"
                            ><v-card-text
                                ><h5 class="stitch-modal__title">
                                    <v-icon size="16">mdi-warehouse</v-icon
                                    ><span>{{
                                        $translate("labels.restrictToStocks")
                                    }}</span>
                                </h5>
                                <p class="text-caption mb-0">
                                    {{
                                        $translate(
                                            "labels.restrictToStocksHint"
                                        )
                                    }}
                                </p>
                                <v-row class="mt-0">
                                    <v-col cols="12" sm="6">
                                        <!-- Selected stocks list with drag and drop -->
                                        <v-list
                                            dense
                                            class="pa-0 mb-2"
                                            v-if="selectedStocks.length > 0"
                                        >
                                            <draggable
                                                v-model="selectedStocks"
                                                handle=".drag-handle"
                                                @end="updateStocksOrder"
                                            >
                                                <v-list-item
                                                    v-for="(
                                                        stock, index
                                                    ) in selectedStocks"
                                                    :key="stock.id"
                                                    class="stock-item mb-2"
                                                    style="
                                                        border: 1px solid
                                                            #e0e0e0;
                                                        border-radius: 4px;
                                                        min-height: 40px;
                                                        height: 40px;
                                                    "
                                                >
                                                    <v-list-item-icon
                                                        class="drag-handle my-0 mr-2"
                                                        style="
                                                            cursor: move;
                                                            align-self: center;
                                                        "
                                                    >
                                                        <v-icon size="20"
                                                            >mdi-drag</v-icon
                                                        >
                                                    </v-list-item-icon>
                                                    <v-list-item-content
                                                        class="py-0"
                                                    >
                                                        <v-list-item-title
                                                            style="
                                                                line-height: 1.2;
                                                            "
                                                        >
                                                            {{
                                                                getStockName(
                                                                    stock
                                                                )
                                                            }}
                                                        </v-list-item-title>
                                                    </v-list-item-content>
                                                    <div
                                                        class="text-caption grey--text mr-3"
                                                        style="
                                                            align-self: center;
                                                            white-space: nowrap;
                                                        "
                                                    >
                                                        {{
                                                            $translate(
                                                                "labels.priority"
                                                            )
                                                        }}: {{ index + 1 }}
                                                    </div>
                                                    <v-list-item-action
                                                        class="my-0"
                                                        style="
                                                            align-self: center;
                                                        "
                                                    >
                                                        <v-btn
                                                            icon
                                                            x-small
                                                            @click="
                                                                removeStock(
                                                                    stock
                                                                )
                                                            "
                                                        >
                                                            <v-icon small
                                                                >mdi-delete</v-icon
                                                            >
                                                        </v-btn>
                                                    </v-list-item-action>
                                                </v-list-item>
                                            </draggable>
                                        </v-list>
                                        <label
                                            class="catalog-field-label"
                                            for="catalog-stock"
                                        >
                                            {{ $translate("labels.addStock") }}
                                        </label>
                                        <v-combobox
                                            :key="selectedStockObject?.id"
                                            v-model="selectedStockObject"
                                            :items="availableStocks"
                                            :item-text="getStockName"
                                            item-value="id"
                                            id="catalog-stock"
                                            :loading="stocksLoading"
                                            :search-input.sync="
                                                stockSearchInput
                                            "
                                            hide-selected
                                            dense
                                            clearable
                                            outlined
                                            @change="addStock"
                                            @click:clear="
                                                selectedStockObject = null
                                            "
                                            class="mt-2"
                                        >
                                            <template v-slot:item="{ item }">
                                                <v-list-item-content>
                                                    <v-list-item-title>{{
                                                        getStockName(item)
                                                    }}</v-list-item-title>
                                                </v-list-item-content>
                                            </template>
                                            <template v-slot:no-data>
                                                <v-list-item>
                                                    <v-list-item-content>
                                                        <v-list-item-title>
                                                            {{
                                                                getNoDataMessage()
                                                            }}
                                                        </v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </template>
                                        </v-combobox>
                                    </v-col>
                                </v-row>
                            </v-card-text></v-card
                        ></template
                    >
                </div>
            </div></template
        >

        <template slot="bottom">
            <v-btn
                class="my-2 my-sm-4 w-100 w-sm-auto"
                text
                :disabled="catalog_saving"
                @click="cancelEditClicked"
            >
                {{ $translate("cancel") }}
            </v-btn>
            <v-btn
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                color="primary"
                :disabled="!readyforsave || isConnectedSeller()"
                :loading="catalog_saving"
                @click="saveInformationClicked"
            >
                {{ saveText }}
            </v-btn>
        </template>
    </ModalContent>
</template>

<script>
import axios from "axios";
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import GlobalMixins from "@/mixins/global";
import SearchProvider from "@/components/common/search-provider.vue";
import AssignCatalog from "@/modals/assigncatalog.vue";
import AddToCatalog from "@/modals/addtocatalog.vue";
import draggable from "vuedraggable";

export default {
    mixins: [GlobalMixins],
    components: {
        SearchProvider,
        draggable,
    },
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            selectedStock: null,
            selectedStockObject: null,
            selectedStocks: [],
            allStocks: [],
            stocksLoading: false,
            stockSearchInput: '',
            pricesIncludeVatTouched: false,
            defaultCatalogTouched: false,
            autoAddProductsTouched: false,
        };
    },
    computed: {
        ...mapState("editcatalog", [
            "catalog",
            "catalog_saving",
            "catalog_saving_errors",
        ]),
        ...mapState("session", ["stats"]),
        ...mapGetters("session", ["current_entity_persona"]),

        defaultPricesIncludeVat() {
            return ["retailer", "hospitality_group"].includes(this.current_entity_persona);
        },

        saveText() {
            if (this.catalog_saving) {
                if (this.isNew && this.catalog.auto_add_products) {
                    return this.$translate("creatingCatalogWithProducts");
                }
                return this.$translate("savingInProgress");
            }
            if (this.isNew) {
                return this.$translate("createCatalog");
            }
            return this.$translate("saveInformations");
        },
        savingTitle() {
            if (this.isNew) {
                return this.$translate("messages.creatingCatalog");
            }
            return this.$translate("messages.savingCatalog");
        },
        savingMessage() {
            if (this.isNew && this.catalog.auto_add_products) {
                return this.$translate("messages.creatingCatalogWithProducts");
            }
            return this.$translate("messages.savingCatalogHint");
        },
        forceVatRate() {
            return this.catalog.force_vat_rate
                ? new this.decimal(this.catalog.force_vat_rate)
                      .times(100)
                      .toString()
                : null;
        },
        targetMarginPercentDisplay: {
            get() {
                return this.catalog.target_margin_percent
                    ? new this.decimal(this.catalog.target_margin_percent)
                          .times(100)
                          .toString()
                    : null;
            },
            set(value) {
                if (value) {
                    try {
                        this.catalog.target_margin_percent = new this.decimal(
                            this.$utils.normalizeDecimalString(value)
                        )
                            .div(100)
                            .toNumber();
                    } catch (e) {
                        // Ignore partial input (e.g. "12,").
                    }
                } else {
                    this.catalog.target_margin_percent = null;
                }
            },
        },
        agentCommissionPercentDisplay: {
            get() {
                return this.catalog.agent_commission_percent
                    ? new this.decimal(this.catalog.agent_commission_percent)
                          .times(100)
                          .toString()
                    : null;
            },
            set(value) {
                if (value) {
                    try {
                        this.catalog.agent_commission_percent = new this.decimal(
                            this.$utils.normalizeDecimalString(value)
                        )
                            .div(100)
                            .toNumber();
                    } catch (e) {
                        // Ignore partial input (e.g. "12,").
                    }
                } else {
                    this.catalog.agent_commission_percent = null;
                }
            },
        },
        defaultVatRate() {
            return this.current_entity_settings.default_vat_rate * 100;
        },
        isNew() {
            return !this.params.catalog?.id;
        },
        priceTypeItems() {
            return [
                { value: true, text: this.$translate('labels.pricesInclVat') },
                { value: false, text: this.$translate('labels.pricesExclVat') },
            ];
        },
        yesNoItems() {
            return [
                { value: true, text: this.$translate('yes') },
                { value: false, text: this.$translate('no') },
            ];
        },
        pricesIncludeVatSelected() {
            return (
                this.catalog.prices_include_vat === true
                || this.catalog.prices_include_vat === false
            );
        },
        pricesIncludeVatErrors() {
            if (this.catalog_saving_errors.prices_include_vat) {
                return this.catalog_saving_errors.prices_include_vat;
            }
            if (this.isNew && this.pricesIncludeVatTouched && !this.pricesIncludeVatSelected) {
                return this.$translate('errors.pricesIncludeVatRequired');
            }
            return null;
        },
        defaultCatalogSelected() {
            return this.catalog.default === true || this.catalog.default === false;
        },
        defaultCatalogErrors() {
            if (this.catalog_saving_errors.default) {
                return this.catalog_saving_errors.default;
            }
            if (this.isNew && this.defaultCatalogTouched && !this.defaultCatalogSelected) {
                return this.$translate('errors.assignToNewCustomersRequired');
            }
            return null;
        },
        autoAddProductsSelected() {
            return (
                this.catalog.auto_add_products === true
                || this.catalog.auto_add_products === false
            );
        },
        autoAddProductsErrors() {
            if (this.catalog_saving_errors.auto_add_products) {
                return this.catalog_saving_errors.auto_add_products;
            }
            if (this.isNew && this.autoAddProductsTouched && !this.autoAddProductsSelected) {
                return this.$translate('errors.autoAddProductsRequired');
            }
            return null;
        },
        autoAddProductsLabel() {
            if (this.catalog.copy_from) {
                return this.$translate('labels.autoAddFutureProducts');
            }
            return this.$translate('labels.addAllExistingProducts');
        },
        autoAddProductsHint() {
            if (this.catalog.copy_from) {
                return this.$translate('labels.autoAddFutureProductsHint');
            }
            return this.$translate('labels.addAllExistingProductsHint');
        },
        shouldOpenAddProductsAfterCreate() {
            return (
                !this.catalog.auto_add_products
                && !this.catalog.copy_from
                && !!this.stats?.products
            );
        },
        readyforsave() {
            if (this.isConnectedSeller()) {
                return false;
            }
            if (!this.catalog.name) {
                return false;
            }
            return true;
        },
        createFormValid() {
            if (!this.isNew) {
                return true;
            }
            return (
                this.pricesIncludeVatSelected
                && this.defaultCatalogSelected
                && this.autoAddProductsSelected
            );
        },
        title() {
            return this.params.catalog?.id
                ? this.$translate("editTitle")
                : this.$translate("createTitle");
        },
        availableStocks() {
            // Filter out already selected stocks and sort by name
            const selectedIds = this.selectedStocks.map(s => s.id);
            return this.allStocks
                .filter(stock => !selectedIds.includes(stock.id))
                .sort((a, b) => {
                    const nameA = this.getStockName(a).toLowerCase();
                    const nameB = this.getStockName(b).toLowerCase();
                    return nameA.localeCompare(nameB);
                });
        },
    },
    created() {
        const isNewCatalog = !this.params.catalog?.id;
        const hasExistingCatalogs = !!this.stats?.catalogs;
        let catalog = {
            default: isNewCatalog ? null : true,
            auto_add_products: isNewCatalog ? null : false,
            ...this.params.catalog,
        };
        if (isNewCatalog && !catalog.name) {
            catalog.name = hasExistingCatalogs
                ? ""
                : this.$translate("defaultCatalogName");
        }
        if (isNewCatalog) {
            if (catalog.default === undefined) {
                catalog.default = null;
            }
            if (catalog.auto_add_products === undefined) {
                catalog.auto_add_products = null;
            }
        }
        if (isNewCatalog && catalog.prices_include_vat === undefined) {
            catalog.prices_include_vat = this.defaultPricesIncludeVat;
        } else if (catalog.prices_include_vat === undefined) {
            catalog.prices_include_vat = false;
        }

        if (this.params.duplicate) {
            catalog.copy_from = this.params.duplicate;
        }
        this.init({ catalog });

        if (catalog.provider_data) {
            this.updateProvider(catalog.provider_data);
        } else if (catalog.provider) {
            this.hydrateProviderFromId(catalog.provider);
        }
        
        // Initialize selected stocks from catalog data
        if (catalog.stocks_data && catalog.stocks_data.length > 0) {
            this.selectedStocks = [...catalog.stocks_data];
        }
        
        // Load all stocks
        this.loadStocks();
    },
    methods: {
        ...mapActions("mcl", ["hideModal", "showModal"]),
        ...mapActions("editcatalog", ["saveCatalog", "init"]),
        ...mapActions("session", ["fetchStats"]),
        ...mapMutations("editcatalog", ["updateProvider", "updateCatalog"]),
        isConnectedSeller() {
            const provider = this.catalog && this.catalog.provider_data;
            if (!provider) {
                return false;
            }
            return Boolean(provider.entity || provider.entity_data);
        },
        async hydrateProviderFromId(providerId) {
            if (!providerId) {
                return;
            }
            try {
                const response = await axios.get(`/api/providers/${providerId}/`);
                this.updateProvider(response.data);
            } catch (error) {
                console.error("Error loading provider:", error);
            }
        },
        catalogSelected(catalog) {
            this.updateCatalog({
                ...catalog,
                id: null,
                copy_from: catalog.id,
                name: !this.catalog.name
                    ? catalog.name + " " + this.$translate("copy")
                    : this.catalog.name,
                force_vat_rate: catalog.force_vat_rate,
                prices_include_vat: catalog.prices_include_vat,
                default: catalog.default,
            });
        },
        getCatalogsParams() {
            if (this.catalog.provider) {
                return {
                    owner: this.current_entity_id,
                    provider: this.catalog.provider,
                    ordering: "provider_name",
                };
            }
            return {
                owned: true,
                provider: this.catalog.provider,
                owner: this.current_entity_id,
                ordering: "provider_name",
            };
        },
        getStockName(stock) {
            const name = stock.name || stock.id;
            // Add owner name if stock is not owned by current user
            if (stock.owned === false && stock.owner_data?.name) {
                return `${name} (${stock.owner_data.name})`;
            }
            return name;
        },
        getNoDataMessage() {
            // If all stocks are selected
            if (this.allStocks.length > 0 && this.availableStocks.length === 0) {
                return this.$translate('labels.allStocksSelected');
            }
            // If user is searching
            if (this.stockSearchInput) {
                return this.$translate('labels.noStocksFound');
            }
            // Default message
            return this.$translate('labels.typeToSearch');
        },
        async loadStocks() {
            this.stocksLoading = true;
            try {
                const response = await axios.get('/api/stocks/', {
                    params: { 
                        ordering: 'name',
                        limit: 1000
                    }
                });
                this.allStocks = response.data.results || [];
            } catch (error) {
                console.error('Error loading stocks:', error);
            } finally {
                this.stocksLoading = false;
            }
        },
        addStock(stock) {
            if (!stock || typeof stock === 'string') {
                this.selectedStockObject = null;
                this.stockSearchInput = '';
                return;
            }

            // Check if stock is already selected
            if (this.selectedStocks.some((s) => s.id === stock.id)) {
                this.selectedStockObject = null;
                this.stockSearchInput = '';
                return;
            }

            // Add stock to selected stocks
            this.selectedStocks.push(stock);
            
            // Reset selection and input
            this.$nextTick(() => {
                this.selectedStockObject = null;
                this.stockSearchInput = '';
            });
        },
        removeStock(stock) {
            this.selectedStocks = this.selectedStocks.filter(
                (s) => s.id !== stock.id
            );
        },
        updateStocksOrder() {
            // Method called when drag and drop ends
            // selectedStocks is already updated by v-model
        },

        cancelEditClicked() {
            this.hideModal();
        },
        requiredLabel(label) {
            return `${label} (*)`;
        },
        markCreateRequiredFieldsTouched() {
            this.pricesIncludeVatTouched = true;
            this.defaultCatalogTouched = true;
            this.autoAddProductsTouched = true;
        },
        async saveInformationClicked() {
            if (this.isConnectedSeller()) {
                return;
            }
            if (this.isNew && !this.createFormValid) {
                this.markCreateRequiredFieldsTouched();
                return;
            }
            try {
                // Prepare catalog data with stock_ids
                const catalogData = {
                    ...this.catalog,
                    stock_ids: this.selectedStocks.map((s) => s.id),
                };

                const response = await this.saveCatalog({
                    instance: catalogData,
                });
                if (response.status == 201) {
                    this.addAlert(this.$translate("catalogCreated"));
                    // Recharger les stats après création d'un catalogue
                    await this.fetchStats();
                } else {
                    this.addAlert(this.$translate("catalogUpdated"));
                }
                if (response.status == 201 && this.shouldOpenAddProductsAfterCreate) {
                    await this.showModal({
                        component: AddToCatalog,
                        params: {
                            catalog: response.data,
                        },
                    });
                }
                if (this.isNew && this.stats.customers && !this.params.skipCustomerAssignment) {
                    await this.showModal({
                        component: AssignCatalog,
                        params: {
                            catalog: response.data,
                            assign_all: this.catalog.default,
                        },
                    });
                }
                this.hideModal({ result: response.data });
            } catch (error) {
                this.$swal({
                    title: this.$translate("failedSavingCatalog"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
    },
    i18n: {
        messages: {
            en: {
                informations: "Informations",
                yes: "Yes",
                no: "No",
                cancel: "Cancel",
                createCatalog: "Create catalog",
                saveInformations: "Save informations",
                savingInProgress: "Saving…",
                creatingCatalogWithProducts: "Creating catalog…",
                catalogCreated: "Catalog created",
                catalogUpdated: "Catalog updated",
                failedSavingCatalog: "Failed saving catalog",
                seller: "Selling as a sales agent",
                labels: {
                    seller: "Seller (optional)",
                    forceVatRate: "Force VAT ({vat} by default%)",
                    priceType: "Price type",
                    pricesInclVat: "Incl. VAT (TTC)",
                    pricesExclVat: "Excl. VAT (HT)",
                    selectPriceType: "Select a price type",
                    selectYesNo: "Select an option",
                    pricesIncludeVat: "Prices include VAT",
                    copyFrom: "Copy from an existing catalog",
                    name: "Catalog name",
                    defaultCatalog: "Assign this catalog to all new customers",
                    assignToNewCustomers:
                        "Assign this catalog to all new customers?",
                    assignToNewCustomersHint:
                        "If yes, every newly created customer will get this catalog automatically.",
                    autoAddProducts:
                        "Automatically add products to this catalog",
                    addAllExistingProducts:
                        "Add all existing products to this catalog now?",
                    addAllExistingProductsHint:
                        "If yes, all current sales products are added and future products will be added automatically. If no, you can pick products right after creation.",
                    autoAddFutureProducts:
                        "Automatically add new products to this catalog later?",
                    autoAddFutureProductsHint:
                        "Products are already copied from the source catalog. Choose yes to also add future products automatically.",
                    targetMarginPercent: "Target margin (%)",
                    targetMarginPercentHint:
                        "Minimum margin as percentage (e.g., 15 for 15%)",
                    agentCommissionPercent: "Agent commission (%)",
                    agentCommissionPercentHint:
                        "Agent commission percentage applied to all catalog entries (e.g., 10 for 10%). Leave empty to define commissions manually.",
                    restrictToStocks:
                        "Restrict shipments",
                    restrictToStocksHint:
                        "Select and order warehouses by preference. Leave empty to allow shipments from all warehouses.",
                    addStock: "Add a warehouse",
                    priority: "Priority",
                    noStocksSelected:
                        "No warehouses selected. All warehouses are allowed.",
                    noStocksFound: "No warehouses found",
                    typeToSearch: "Type to search warehouses",
                    allStocksSelected: "All warehouses have been added to the list",
                    allStocksSelectedHint:
                        "All warehouses have been added to the list. You can remove any warehouse from the list if you don't want to allow shipments from it.",
                },
                sellerInstructions:
                    "You can sell a supplier's products as a sales agent by creating a sales catalog for that supplier.",
                editTitle: "Edit sales catalog",
                createTitle: "Create a sales catalog",
                connectedSellerInstructions:
                    "The supplier you selected is connected to you on Safetee. To sell their products as a sales agent, you need to ask them to add you as a sales agent on one or more of their sales catalogs.",
                copy: " (copy)",
                defaultCatalogName: "Default catalog",
                errors: {
                    pricesIncludeVatRequired:
                        "Please choose whether catalog prices are incl. or excl. VAT.",
                    assignToNewCustomersRequired:
                        "Please choose whether to assign this catalog to all new customers.",
                    autoAddProductsRequired:
                        "Please choose whether to add existing products to this catalog.",
                },
                messages: {
                    creatingCatalog: "Creating catalog…",
                    savingCatalog: "Saving catalog…",
                    creatingCatalogWithProducts:
                        "Products are being added to the catalog. This may take up to a minute for large product lists.",
                    savingCatalogHint:
                        "Please wait while your changes are saved.",
                },
            },
            fr: {
                informations: "Informations",
                yes: "Oui",
                no: "Non",
                cancel: "Annuler",
                createCatalog: "Créer le catalogue",
                saveInformations: "Sauvegarder les informations",
                savingInProgress: "Enregistrement…",
                creatingCatalogWithProducts: "Création du catalogue…",
                catalogCreated: "Catalogue créé",
                catalogUpdated: "Catalogue mis à jour",
                failedSavingCatalog: "Impossible de sauvegarder le catalogue",
                seller: "Vente en tant qu'agent commercial",
                labels: {
                    seller: "Vendeur (optionnel)",
                    forceVatRate: "Forcer la TVA ({vat} par défaut%)",
                    priceType: "Type de prix",
                    pricesInclVat: "TTC",
                    pricesExclVat: "HT",
                    selectPriceType: "Sélectionner un type de prix",
                    selectYesNo: "Sélectionner une option",
                    pricesIncludeVat: "Prix TTC",
                    copyFrom: "Copier depuis un catalogue existant",
                    name: "Nom du catalogue",
                    defaultCatalog:
                        "Assigner ce catalogue à tous les nouveaux clients",
                    assignToNewCustomers:
                        "Assigner ce catalogue à tous les nouveaux clients ?",
                    assignToNewCustomersHint:
                        "Si oui, chaque nouveau client créé recevra automatiquement ce catalogue.",
                    autoAddProducts:
                        "Ajouter automatiquement les produits à ce catalogue",
                    addAllExistingProducts:
                        "Ajouter tous les produits existants à ce catalogue maintenant ?",
                    addAllExistingProductsHint:
                        "Si oui, tous les produits de vente actuels sont ajoutés et les futurs produits le seront aussi automatiquement. Si non, vous pourrez choisir les produits juste après la création.",
                    autoAddFutureProducts:
                        "Ajouter automatiquement les nouveaux produits à ce catalogue plus tard ?",
                    autoAddFutureProductsHint:
                        "Les produits sont déjà copiés depuis le catalogue source. Choisissez oui pour aussi ajouter automatiquement les futurs produits.",
                    targetMarginPercent: "Marge cible (%)",
                    targetMarginPercentHint:
                        "Marge minimum en pourcentage (ex: 15 pour 15%)",
                    agentCommissionPercent: "Commission agent (%)",
                    agentCommissionPercentHint:
                        "Pourcentage de commission agent appliqué à toutes les entrées du catalogue (ex: 10 pour 10%). Laissez vide pour définir les commissions manuellement.",
                    restrictToStocks:
                        "Restreindre les expéditions",
                    restrictToStocksHint:
                        "Vous pouvez restreindre les expéditions à certains entrepôts. Les stocks affichés pendant la prise de commande seront automatiquement calculés en fonction des stocks disponibles dans les entrepôts sélectionnés. Sélectionnez et ordonnez les stocks par ordre de préférence. Laissez vide pour autoriser les expéditions depuis tous les stocks.",
                    addStock: "Ajouter un stock",
                    priority: "Priorité",
                    noStocksSelected:
                        "Aucun stock sélectionné. Tous les stocks sont autorisés.",
                    noStocksFound: "Aucun stock trouvé",
                    typeToSearch: "Tapez pour rechercher des stocks",
                    allStocksSelected: "Tous les stocks ont été ajoutés à la liste",
                    allStocksSelectedHint:
                        "Tous les stocks ont été ajoutés à la liste. Vous pouvez retirer un stock de la liste si vous ne souhaitez pas autoriser les expéditions depuis celui-ci.",
                },
                sellerInstructions:
                    "Vous pouvez vendre les produits d'un fourniseur en tant qu'agent commercial en créant un catalogue de vente pour ce fournisseur.",
                editTitle: "Modifier le catalogue de vente",
                createTitle: "Créer un catalogue de vente",
                connectedSellerInstructions:
                    "Le fournisseur que vous avez selectionné est connecté avec vous sur Safetee. Pour vendre ses produits en tant qu'agent commercial, vous devez lui demander de vous ajouter en tant qu'agent commercial sur un ou plusieurs de ses catalogues de vente.",
                copy: " (copie)",
                defaultCatalogName: "Catalogue par défaut",
                errors: {
                    pricesIncludeVatRequired:
                        "Veuillez choisir si les prix du catalogue sont TTC ou HT.",
                    assignToNewCustomersRequired:
                        "Veuillez indiquer si ce catalogue doit être assigné à tous les nouveaux clients.",
                    autoAddProductsRequired:
                        "Veuillez indiquer si les produits existants doivent être ajoutés à ce catalogue.",
                },
                messages: {
                    creatingCatalog: "Création du catalogue en cours…",
                    savingCatalog: "Enregistrement du catalogue…",
                    creatingCatalogWithProducts:
                        "Les produits sont en cours d'ajout au catalogue. Cela peut prendre jusqu'à une minute pour un grand nombre de produits.",
                    savingCatalogHint:
                        "Veuillez patienter pendant l'enregistrement.",
                },
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/_stitch-form.scss";
.catalog-saving-alert {
    margin-bottom: 16px;
}

.catalog-modal-form--saving {
    opacity: 0.55;
    pointer-events: none;
    user-select: none;
}

.catalog-field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 6px;
}
</style>
